import { call, put, select, takeLatest } from 'redux-saga/effects';
import { Response } from '@types';
import { PayloadAction } from '@reduxjs/toolkit';
import { AxiosResponse } from 'axios';

import * as actions from '.';
import * as services from '../../services';
import {
  CategoryResponse,
  OptInAddressData,
  OptInPersonalData,
  OptInResponse,
  Pharmaceutical,
  PharmaceuticalCategory,
  PharmaPayload,
  PharmasResponse,
} from '../../services/types';
import {
  getOptInAddressData,
  getOptInPersonalData,
  getSelectedCategories,
  getSelectedPharmas,
} from './selectors';
import { Clinic } from 'features/CFMUpdate/types';
import { getClinicDetails } from 'features/CFMUpdate/services';

type SendIntercomEventPayload = ReturnType<typeof actions.sendIntercomEvent>;

export function* setOptInConfirmationWorker() {
  try {
    const selectedPharmas: Pharmaceutical[] = yield select(getSelectedPharmas);
    const personalData: OptInPersonalData = yield select(getOptInPersonalData);
    const addressData: OptInAddressData = yield select(getOptInAddressData);

    const formattedPharmas: PharmaPayload[] = selectedPharmas.map((pharma) => ({
      id: pharma.id,
      name: pharma.name,
    }));

    const selectedCategories: PharmaceuticalCategory[] = yield select(
      getSelectedCategories,
    );
    const selectedCategoriesIds = selectedCategories.map(
      (category) => category.id,
    );

    const { existing_clinic_address, ...cleanedPersonalData } = personalData;

    const payload = {
      ...cleanedPersonalData,
      ...addressData,
      pharmas: formattedPharmas,
      therapeutic_categories: selectedCategoriesIds,
    };

    const response: AxiosResponse<OptInResponse> = yield call(
      services.setFreeSamplesOptIn,
      payload,
    );

    if (response) {
      yield put(actions.submitOptInConfirmationSuccess());
    } else {
      yield put(actions.submitOptInConfirmationFailure());
    }
  } catch (error) {
    yield put(actions.submitOptInConfirmationFailure());
  }
}

export function* fetchPharmasDataWorker() {
  try {
    yield put(actions.fetchPharmasStart());

    const { data }: Response<PharmasResponse> = yield call(services.getPharmas);

    yield put(actions.fetchPharmasSuccess(data.objects));
  } catch (error) {
    yield put(actions.fetchPharmasFailure());
  }
}

export function* fetchCategoriesDataWorker() {
  try {
    yield put(actions.fetchCategoriesStart());

    const { data }: Response<CategoryResponse> = yield call(
      services.getCategories,
    );

    yield put(actions.fetchCategoriesSuccess(data.objects));
  } catch (error) {
    yield put(actions.fetchCategoriesFailure());
  }
}

export function* sendIntercomEventWorker({
  payload: event,
}: SendIntercomEventPayload) {
  try {
    const response: Response<OptInResponse> = yield call(
      services.sendIntercomEvent,
      event,
    );

    if (response) {
      yield put(actions.sendIntercomEventSuccess(event));
    } else {
      yield put(actions.sendIntercomEventFailure());
    }
  } catch (error) {
    yield put(actions.sendIntercomEventFailure());
  }
}

export function* fetchClinicAddressWorker(action: PayloadAction<number>) {
  try {
    const clinicId = action.payload;

    const { data }: AxiosResponse<Clinic> = yield call(
      getClinicDetails,
      clinicId,
    );

    if (data) {
      yield put(
        actions.setOptInAddressData({
          phone_number: '',
          zip_code: data.zip_code,
          address: data.address,
          number: String(data.number),
          complement: data.complement,
          neighborhood: data.neighborhood,
          city: data.city,
          state: data.state,
          is_clinic_address: false,
        }),
      );
    }
  } catch (error) {
    yield put(actions.submitOptInConfirmationFailure());
  }
}

export default function* freeSamplesOptInSagas() {
  yield takeLatest(actions.setOptInConfirmation, setOptInConfirmationWorker);
  yield takeLatest(actions.fetchPharmasData, fetchPharmasDataWorker);
  yield takeLatest(actions.sendIntercomEvent, sendIntercomEventWorker);
  yield takeLatest(actions.fetchClinicAddress, fetchClinicAddressWorker);
  yield takeLatest(actions.fetchCategoriesData, fetchCategoriesDataWorker);
}
